import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { MergeDevAPIService } from 'app/shared/api/mergedev.api.service';
import { MergeDevLinkTokenResponse } from '@models/integrations/mergedev/merge-dev-link-token.model';
import { CustomWindow } from '@models/custom-window.model';
import { forkJoin } from 'rxjs';
import { IState } from '@app/models/state/state.model';
import { ButtonType } from 'app/shared/components/inputs/button/button.component';
import { PillType } from 'app/shared/components/pill/pill.component';
import { ProvisionedUser } from '@app/models/user/provisioned-user.model';
import { ExternalProvisioner } from '@app/models/user/external-provisioner';
import { UserAPIService } from '@app/shared/api/user.api.service';
import { User } from '@app/models/user/user.model';
import { Breadcrumb } from '@app/models/breadcrumb.model';
import { BreadcrumbService } from '@app/shared/breadcrumbs/breadcrumbs.service';
import { ActivatedRoute } from '@angular/router';
import { MergeDevLinkedAccount } from '@app/models/integrations/mergedev/merge-dev-linked-account.model';
import { MergeDevTicketInfoByUserIdResponse } from '@app/models/integrations/mergedev/ticketing/merge-dev-ticket-info-by-user-id.model';
import { MergeDevTicketingUserStats } from '@app/models/integrations/mergedev/ticketing/merge-dev-ticketing-user-stats.model';


declare let window: CustomWindow;

interface ComponentProvisionedUser extends ProvisionedUser {
  user: User;
}

@Component({
  selector: 'app-merge-integration-component',
  templateUrl: './integration-merge.component.html',
  styleUrls: ['./integration-merge.component.scss']
})
export class IntegrationMergeComponent implements OnInit, OnDestroy {
  public readonly eButtonType = ButtonType;
  public readonly ePillType = PillType;

  state: IState;
  breadcrumb: Breadcrumb;

  linkToken: MergeDevLinkTokenResponse;
  provisionedUsers: {
    master: ComponentProvisionedUser[],
    page: ComponentProvisionedUser[]
  };
  linkedAccounts: MergeDevLinkedAccount[];
  ticketingStatsForUser: MergeDevTicketingUserStats;

  constructor(
    public activatedRoute: ActivatedRoute,
    private mergeAPIService: MergeDevAPIService,
    private userAPIService: UserAPIService,
    private breadcrumbService: BreadcrumbService,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    this.state = {
      loading: true,
      error: false,
      errorMessage: ''
    };

    this.linkToken = undefined;
    this.provisionedUsers = {
      master: [],
      page: []
    };
    this.linkedAccounts = [];
    this.breadcrumb = this.breadcrumbService.init(this.activatedRoute);
    this.ticketingStatsForUser = undefined;
  }

  ngOnInit() {
    this.getData();
  }

  ngOnDestroy(): void {
    this.breadcrumbService.remove(this.breadcrumb);
  }

  getData(): void {
    this.state.loading = true;
    this.linkToken = undefined;

    forkJoin([
      this.mergeAPIService.getLinkToken(),
      this.mergeAPIService.getLinkedAccounts()
    ]).subscribe(([linkTokenResponse, linkedAccounts]) => {
      this.linkToken = linkTokenResponse;
      this.linkedAccounts = linkedAccounts;

      this.initMergeLink(linkTokenResponse.link_token);
      this.getProvisionedUsers();
    });

  }

  doError(message: string): void {
    this.state.loading = false;
    this.state.error = true;
    this.state.errorMessage = message;
  }

  initMergeLink(linkToken: string): void {
    window.MergeLink.initialize({
      linkToken: linkToken,
      onSuccess: (public_token) => this.onSuccess(public_token),
      onReady: () => (this.onReady()),
      onExit: () => (this.getData()),
      shouldSendTokenOnSuccessfulLink: true,
      tenantConfig: {apiBaseURL: 'https://api-eu.merge.dev'}
    });
  }

  onReady(): void {
    console.info('Merge link initialised');
    this.state.loading = false;
    this.changeDetectorRef.detectChanges();
  }

  onSuccess(public_token: string): void {
    console.info('Successfully linked account, token is:', public_token);
    this.mergeAPIService.createAccountTokenFromPublicToken(public_token).subscribe(resp => {
      console.debug('Merge link success:', resp);
    });
  }

  openMergeLink(): void {
    window.MergeLink.openLink();
  }

  getProvisionedUsers(): void {
    this.userAPIService.getAllProvisionedUsersByProvisioner(ExternalProvisioner.MERGE_DEV)
      .toPromise()
      .then(provisionedUsers => {
        provisionedUsers = provisionedUsers.filter(pu => pu.userId);

        let provisionedUserIds = provisionedUsers.map(pu => pu.userId);
        provisionedUserIds = provisionedUserIds.sort((a, b) => a - b); // Sort ascending

        this.userAPIService.getUsersByIds(provisionedUserIds).subscribe(users => {
          let provisionedUsersExpanded = provisionedUsers.map(pu => {
            return {
              ...pu,
              user: users.find(u => u.id === pu.userId)
            };
          });

          // Sort ones with errorMessage populated to the top
          provisionedUsersExpanded = this.sortProvisionedUsers(provisionedUsersExpanded);

          this.provisionedUsers = {
            master: provisionedUsersExpanded,
            page: []
          };
        });
      });
  }

  /**
   * Sort alphabetically but with all ones with errorMessage populated to the top
   * @param provisionedUsers Provisioned users to sort
   * @returns Sorted provisioned users
   */
  sortProvisionedUsers(provisionedUsers: ComponentProvisionedUser[]): ComponentProvisionedUser[] {
    return provisionedUsers.sort((a, b) => {
      if (a.errorMessage && !b.errorMessage) {
        return -1;
      } else if (!a.errorMessage && b.errorMessage) {
        return 1;
      } else {
        const fullName = (user: User) => `${user.firstName} ${user.lastName}`;
        return fullName(a.user).localeCompare(fullName(b.user));
      }
    });
  }
}
